import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaExclamationTriangle, FaSave } from "react-icons/fa";
import Spinner from "../../components/Spinner";
import http from "../../api/apiClient";
import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";

export type LedgerOption = { id: number; code?: string | null; description?: string | null; category?: string | null; selected: boolean };
type LedgersView = { available: boolean; status: string; ledgers: LedgerOption[]; selectedIds: number[] };

const ENDPOINT = "/finance/settings/eboekhouden/ledgers";

/** Which e-Boekhouden ledgers count as parent contribution when payments are read in. */
export default function ContributionLedgers({ canManage, reloadKey }: { canManage: boolean; reloadKey?: string | null }): ReactElement {
  const { t } = useTranslation();
  const [view, setView] = useState<LedgersView | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    http.get(ENDPOINT)
      .then(({ data }) => {
        if (cancelled) return;
        setView(data);
        setSelected(new Set(data?.selectedIds ?? []));
      })
      .catch((caught) => {
        if (!cancelled) setMessage({ ok: false, text: mapHttpErrorToMessage(caught, t).message || "" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t, reloadKey]);

  // Profit-and-loss ledgers (where income is booked) first, then the rest by code.
  const ledgers = useMemo(() => [...(view?.ledgers ?? [])].sort((a, b) =>
    Number(b.category === "VW") - Number(a.category === "VW") || String(a.code ?? "").localeCompare(String(b.code ?? ""), "nl", { numeric: true })),
  [view]);

  const toggle = (id: number) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await http.put(ENDPOINT, { ledgerIds: Array.from(selected) });
      setView(data);
      setSelected(new Set(data?.selectedIds ?? []));
      setMessage({ ok: true, text: t("finance.settings.ledgers.saved", { defaultValue: "Ledgers saved." }) });
    } catch (caught) {
      setMessage({ ok: false, text: mapHttpErrorToMessage(caught, t).message || "" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80 sm:p-6">
      <h2 className="text-base font-bold text-slate-950 dark:text-white">
        {t("finance.settings.ledgers.title", { defaultValue: "Ledgers for the parent contribution" })}
      </h2>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        {t("finance.settings.ledgers.help", {
          defaultValue: "Only 'money received' on these ledgers is read in as contribution. Choose none to read in all money received; then leave out what is not a contribution on the Parent contributions page.",
        })}
      </p>
      {loading ? (
        <Spinner />
      ) : (
        <>
          {view && !view.available && (
            <p role="alert" className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800 dark:bg-amber-400/10 dark:text-amber-200">
              <FaExclamationTriangle aria-hidden="true" className="mt-0.5 shrink-0" />
              {t("finance.settings.ledgers.unavailable", {
                count: (view.selectedIds ?? []).length,
                defaultValue: "The ledgers could not be read from e-Boekhouden. Check the connection above. {{count}} ledger(s) chosen.",
              })}
            </p>
          )}
          {ledgers.length > 0 && (
            <div className="mt-3 grid max-h-80 gap-1 overflow-y-auto rounded-xl border border-slate-200 p-2 dark:border-white/10 sm:grid-cols-2">
              {ledgers.map((ledger) => (
                <label key={ledger.id} className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm hover:bg-slate-50 dark:hover:bg-white/5">
                  <input type="checkbox" checked={selected.has(ledger.id)} disabled={!canManage} onChange={() => toggle(ledger.id)} />
                  <span className="font-mono text-xs text-slate-500">{ledger.code}</span>
                  <span className="text-slate-800 dark:text-slate-100">{ledger.description}</span>
                </label>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            {selected.size === 0
              ? t("finance.settings.ledgers.none_selected", { defaultValue: "No ledger chosen: all money received is read in." })
              : t("finance.settings.ledgers.selected", { count: selected.size, defaultValue: "{{count}} ledger(s) chosen." })}
          </p>
          {canManage && view?.available && (
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="mt-3 inline-flex h-10 items-center gap-2 rounded-xl bg-indigo-600 px-4 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              <FaSave aria-hidden="true" />
              {t("finance.settings.ledgers.save", { defaultValue: "Save ledgers" })}
            </button>
          )}
        </>
      )}
      {message && (
        <p role={message.ok ? "status" : "alert"} className={`mt-3 text-sm font-semibold ${message.ok ? "text-emerald-700 dark:text-emerald-300" : "text-red-600"}`}>
          {message.text}
        </p>
      )}
    </section>
  );
}
